import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import ZoomableImage from '../components/ZoomableImage';
import SEO from '../components/SEO';
import { 
  CheckCircle, 
  ShieldCheck, 
  Award, 
  Sparkles, 
  Factory, 
  Sun, 
  Check, 
  ArrowRight, 
  Upload, 
  X, 
  CheckCircle2, 
  AlertCircle, 
  Loader2, 
  Image as ImageIcon,
  Filter,
  Info
} from 'lucide-react';

export default function VubaMaterialer() {
  // Form states for Vusion by Vuba
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [messageText, setMessageText] = useState('');
  const fileInputRef = useRef(null);

  // File constraints
  const MAX_FILES = 5;
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
  const MAX_TOTAL_SIZE = 20 * 1024 * 1024; // 20 MB
  const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (selectedFiles.length + files.length > MAX_FILES) {
      alert(`Du kan maksimalt uploade ${MAX_FILES} billeder.`);
      return;
    }

    const validFiles = [];
    let currentTotalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);

    for (const file of files) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        alert(`Filtypen "${file.name}" er ikke tilladt. Kun PNG, JPEG og WEBP understøttes.`);
        continue;
      }

      if (file.size > MAX_FILE_SIZE) {
        alert(`Billedet "${file.name}" er for stort. Maksimal størrelse er 10 MB.`);
        continue;
      }

      if (currentTotalSize + file.size > MAX_TOTAL_SIZE) {
        alert(`Den samlede filstørrelse overstiger grænsen på 20 MB.`);
        break;
      }

      currentTotalSize += file.size;
      validFiles.push(file);
    }

    setSelectedFiles((prev) => [...prev, ...validFiles]);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Append files
    formData.delete('billeder');
    selectedFiles.forEach((file) => {
      formData.append('billeder', file);
    });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (response.ok && result.status === 'ok') {
        setStatus('success');
        setSelectedFiles([]);
        form.reset();
      } else {
        setStatus('error');
        setErrorMessage(result.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus('error');
      setErrorMessage('Forbindelsesfejl. Tjek din internetforbindelse og prøv igen.');
    }
  };

  const designCategories = [
    { title: 'Lyse stenblandinger', desc: 'Giver et let, skandinavisk og åbent udtryk, der reflekterer lyset smukt.' },
    { title: 'Mørke stenblandinger', desc: 'Skaber en eksklusiv, dramatisk og moderne kontrast til lyse husfacader.' },
    { title: 'Naturlige nuancer', desc: 'Klassiske jordfarver, der falder harmonisk ind i havens grønne omgivelser.' },
    { title: 'Moderne grå toner', desc: 'Minimalistiske og stilrene nuancer, der passer perfekt til nybyggeri.' },
    { title: 'Varme sandfarver', desc: 'Sydlandsk stemning med bløde, varme og indbydende farvetoner.' },
    { title: 'Eksklusive kontraster', desc: 'Sammensætninger med markante farvespil, der skaber liv og dynamik.' },
    { title: 'Klassiske & tidløse kombinationer', desc: 'Sikre valg, der bevarer deres elegance på tværs af skiftende trends.' }
  ];

  const vubaColors = [
    // Varme & Rødlige
    {
      name: 'Tyrian',
      category: 'warm-red',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/tyrian.webp'
    },
    {
      name: 'Tawny',
      category: 'warm-red',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/tawny.webp'
    },
    {
      name: 'Saxon',
      category: 'warm-red',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/saxon.webp'
    },
    {
      name: 'Red Marble',
      category: 'warm-red',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/red-marble.webp'
    },
    {
      name: 'Red Granit',
      category: 'warm-red',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/red-granit.webp'
    },
    // Unikke & Kontraster
    {
      name: 'Pantagonia',
      category: 'unique-contrast',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/pantagonia.webp'
    },
    {
      name: 'Irish Bronze',
      category: 'unique-contrast',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/irish-bronze.webp'
    },
    {
      name: 'Ipanema Beach',
      category: 'unique-contrast',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/ipanema-beach.webp'
    },
    {
      name: 'Coshel Quartz',
      category: 'unique-contrast',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/coshel-quartz.webp'
    },
    {
      name: 'Ballagio',
      category: 'unique-contrast',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/ballagio.webp'
    },
    // Mørke & Grå
    {
      name: 'Posidon',
      category: 'dark-grey',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/posidon.webp'
    },
    {
      name: 'Nero Marble',
      category: 'dark-grey',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/nero-marble.webp'
    },
    {
      name: 'Gunmetal Quartz',
      category: 'dark-grey',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/gunmetal-quartz.webp'
    },
    {
      name: 'Galahad Grey',
      category: 'dark-grey',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/galahad-grey.webp'
    },
    {
      name: 'Black Bassalt',
      category: 'dark-grey',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/black-bassalt.webp'
    },
    // Lyse & Sandfarvede
    {
      name: 'Tuscan',
      category: 'light-sand',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/tuscan.webp'
    },
    {
      name: 'Tortora Marble',
      category: 'light-sand',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/tortora-marble.webp'
    },
    {
      name: 'St. Ives',
      category: 'light-sand',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/st-ives.webp'
    },
    {
      name: 'Rosa Marble',
      category: 'light-sand',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/rosa-marble.webp'
    },
    {
      name: 'Paloma',
      category: 'light-sand',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/paloma.webp'
    },
    {
      name: 'Necker Island',
      category: 'light-sand',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/necker-island.webp'
    },
    {
      name: 'Ivory Marble',
      category: 'light-sand',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/ivory-marble.webp'
    },
    {
      name: 'Fall Quartz',
      category: 'light-sand',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/fall-quartz.webp'
    },
    {
      name: 'Athena Quartz',
      category: 'light-sand',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/athena-quartz.webp'
    },
    {
      name: 'Alaska Thundra',
      category: 'light-sand',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/alaska-thundra.webp'
    }
  ];

  return (
    <div className="pt-24 md:pt-40 xl:pt-28 pb-16">
      <SEO
        title="Vuba Resin & Stenmaterialer | Førende Kvalitet | Stenbelægning.dk"
        description="Udforsk Vuba resin- og stenmaterialer til professionel stenbelægning. UV-bestandigt bindemiddel og naturlige kvalitetsskærver til indkørsler, terrasser og erhverv."
      />
      {/* Hero Section */}
      <section className="bg-stone-50 py-16 border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-xs font-bold tracking-widest uppercase text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">
              Europas Førende Producent
            </span>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-stone-900 tracking-tight mt-3">
              Kvalitetsmaterialer fra Vuba
            </h1>
            <p className="text-xl text-stone-600 mt-4 font-medium">
              Udviklet til at give en ekstremt stærk, flot og holdbar stenbelægning, der holder i årtier.
            </p>
            <p className="text-stone-500 mt-4 leading-relaxed">
              Vi anvender udelukkende originale kvalitetsprodukter fra britiske Vuba – Europas absolut førende producent af resin bound-belægninger. Vuba har siden 2009 sat nye standarder for kvalitet, holdbarhed og æstetik inden for helstøbte stenbelægninger ved at kombinere de fineste naturlige sten med avanceret, UV-stabil resin.
            </p>
          </div>
        </div>
      </section>

      {/* Core Components Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-stone-900">
                Hemmeligheden bag den ekstreme holdbarhed
              </h2>
              <p className="text-stone-600 leading-relaxed">
                En helstøbt stenbelægning er kun så stærk som de materialer, den er bygget af. Vuba-systemet består af to nøglekomponenter, der er perfekt afstemt efter hinanden:
              </p>

              <div className="space-y-4">
                {/* Sten */}
                <div className="flex gap-4 p-5 bg-stone-50 rounded-2xl border border-stone-200">
                  <div className="p-3 rounded-xl bg-emerald-50 text-emerald-600 shrink-0 h-11 w-11 flex items-center justify-center">
                    <Sparkles className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-stone-900">1. Naturlige, tørrede sten</h4>
                    <p className="text-sm text-stone-600 mt-1 leading-relaxed">
                      Vi bruger udelukkende 100% naturlige kvarts-, granit- og flintsten. Stenene vaskes, sorteres efter størrelse og ovntørres mekanisk hos Vuba for at fjerne alt fugt og støv. Det sikrer en perfekt og uopløselig binding med resinen.
                    </p>
                  </div>
                </div>

                {/* Resin */}
                <div className="flex gap-4 p-5 bg-stone-50 rounded-2xl border border-stone-200">
                  <div className="p-3 rounded-xl bg-emerald-50 text-emerald-600 shrink-0 h-11 w-11 flex items-center justify-center">
                    <Sun className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-stone-900">2. UV-resistent polyurethan-resin</h4>
                    <p className="text-sm text-stone-600 mt-1 leading-relaxed">
                      Vubas bindemiddel is en højkvalitets, tokomponent polyurethan-resin, som er 100% UV-resistent. Det betyder, at belægningen er fuldstændig farveægte og ikke gulner eller svækkes af solens stråler, selv efter mange års hård UV-påvirkning.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Billede: Resin udlægning */}
            <div className="relative overflow-hidden shadow-xl rounded-2xl aspect-video lg:aspect-square">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/6220a5-246ba571671341f2b8d7398be938ffe3-mv2.jpg"
                alt="Udlægning af Vuba resin-belægning med spartel"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Egen produktion & Fabrik */}
      <section className="py-16 bg-stone-50 border-y border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Billede: Vuba Fabrik */}
            <div className="relative overflow-hidden shadow-xl rounded-2xl aspect-video lg:aspect-square order-2 lg:order-1">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/6220a5-767920089bd14120a33c375c905f1364-mv2.webp"
                alt="Vuba fabrikken i England"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="space-y-6 order-1 lg:order-2">
              <div className="flex items-center gap-2 text-emerald-600">
                <Factory className="h-5 w-5" />
                <span className="text-xs font-bold tracking-widest uppercase">Egen Produktion</span>
              </div>
              <h2 className="text-3xl font-bold text-stone-900">
                Fuld kontrol over kvaliteten
              </h2>
              <p className="text-stone-600 leading-relaxed">
                I starten af 2024 investerede Vuba mere end 3,2 millioner pund i et nyt, topmoderne anlæg til forarbejdning af stenmaterialer i Goole, England. Da Vuba råder over egen fabrik, hvor al håndtering, forarbejdning, kemiproduktion og kvalitetskontrol foregår internt, er der fuld sporbarhed og kontrol over hele processen.
              </p>
              <p className="text-stone-600 leading-relaxed">
                For dig som kunde betyder det en række markante fordele:
              </p>

              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <li className="flex items-center gap-2 text-sm text-stone-700">
                  <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                  Høj og ensartet materialekvalitet
                </li>
                <li className="flex items-center gap-2 text-sm text-stone-700">
                  <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                  Større udvalg af farver og blandinger
                </li>
                <li className="flex items-center gap-2 text-sm text-stone-700">
                  <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                  Konkurrencedygtige priser
                </li>
                <li className="flex items-center gap-2 text-sm text-stone-700">
                  <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                  Stabil levering og høj tilgængelighed
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Fordele ved Vuba Materialer */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-stone-900">Hvorfor vi vælger Vuba</h2>
            <p className="text-stone-500 mt-2">
              Vuba-produkterne er testet efter de absolut højeste europæiske standarder og anvendes over hele verden.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Drænende */}
            <div className="bg-white p-6 rounded-2xl border border-stone-200 shadow-sm space-y-2">
              <h4 className="font-bold text-stone-900 text-base">Drænende & frostsikker</h4>
              <p className="text-xs text-stone-600 leading-relaxed">
                Vandet siver direkte igennem belægningen (op til 850 liter pr. m² pr. minut), hvilket forhindrer vandpytter og frostsprængninger.
              </p>
            </div>

            {/* Slidstærk */}
            <div className="bg-white p-6 rounded-2xl border border-stone-200 shadow-sm space-y-2">
              <h4 className="font-bold text-stone-900 text-base">Slidstærk & fleksibel</h4>
              <p className="text-xs text-stone-600 leading-relaxed">
                Blandingen af sten og resin giver en ekstremt slidstærk overflade, der bevarer en let fleksibilitet, så den ikke revner ved sætninger.
              </p>
            </div>

            {/* UV-bestandig */}
            <div className="bg-white p-6 rounded-2xl border border-stone-200 shadow-sm space-y-2">
              <h4 className="font-bold text-stone-900 text-base">UV-bestandig & farveægte</h4>
              <p className="text-xs text-stone-600 leading-relaxed">
                Den avancerede resin modstår solens UV-stråler, så belægningen bevarer sin oprindelige, smukke farve år efter år uden at falme.
              </p>
            </div>

            {/* Flot finish */}
            <div className="bg-white p-6 rounded-2xl border border-stone-200 shadow-sm space-y-2">
              <h4 className="font-bold text-stone-900 text-base">Flot finish & design</h4>
              <p className="text-xs text-stone-600 leading-relaxed">
                Vælg mellem mere end 150 forskellige farve- og designkombinationer, der kan tilpasses perfekt til din ejendoms arkitektur.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: Farve-teaser og link til Farver og Design */}
      <section id="farver" className="py-20 bg-stone-50 border-y border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl border border-stone-200 p-8 sm:p-12 shadow-sm grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-bold tracking-widest uppercase text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">
                Eksklusivt Farveudvalg
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
                Udforsk vores 25 unikke farver og stenblandinger
              </h2>
              <p className="text-stone-600 text-base leading-relaxed">
                Vi tilbyder hele den originale <strong>Vuba Europe-kollektion</strong>. Hvad end du er til lyse skandinaviske sandfarver, klassiske naturtoner eller moderne mørke kontraster, har vi den ideelle stenblanding.
              </p>
              <div className="pt-2">
                <Link
                  to="/farver-og-design"
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-sm shadow-sm transition-all duration-200 group"
                >
                  Se alle farver og design
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 relative overflow-hidden rounded-2xl aspect-square shadow-md border border-stone-100">
              <ZoomableImage
                src="https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/6220a5-03b6d8988038445f889647a00d1e7e3f-mv2.jpg"
                alt="Vuba farveudvalg og stenblandinger"
                className="w-full h-full rounded-2xl"
                zoomScale={2.5}
                optimizedWidth={800}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Designkategorier */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-stone-900">Designmuligheder til enhver stil</h2>
            <p className="text-stone-500 mt-2">
              Uanset om du bor i en klassisk patriciervilla, et moderne funkis-hus eller en hyggelig landejendom, har vi en stenblanding, der passer til.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {designCategories.map((cat, idx) => (
              <div key={idx} className="p-6 bg-stone-50 rounded-2xl border border-stone-200 space-y-2">
                <h3 className="font-bold text-stone-900 text-base flex items-center gap-2">
                  <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                  {cat.title}
                </h3>
                <p className="text-stone-600 text-xs leading-relaxed">{cat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sektion: Vusion by Vuba (Digital Visualisering) */}
      <section id="vusion-form-section" className="py-16 bg-stone-50 border-t border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="flex items-center gap-2 text-emerald-600">
                <Sparkles className="h-5 w-5" />
                <span className="text-xs font-bold tracking-widest uppercase">Digital Visualisering</span>
              </div>
              <h2 className="text-3xl font-bold text-stone-900 leading-tight">
                Send et billede til os og vi forvandler det digitalt med Vusion by Vuba.
              </h2>
              <p className="text-stone-600 leading-relaxed">
                Det kan være svært at forestille sig, hvordan en ny fugefri stenbelægning vil se ud foran netop dit hus. Derfor tilbyder vi en professionel, digital visualisering via det avancerede **Vusion by Vuba** system.
              </p>
              <p className="text-stone-600 leading-relaxed">
                Alt du skal gøre er at tage et godt billede af din nuværende indkørsel, terrasse eller gangsti og sende det til os via formularen. Vores designere vil derefter digitalt lægge forskellige stenbelægninger, farver og designs ind på dit billede. Det gør det nemt og trygt for dig at træffe den helt rigtige beslutning, før arbejdet går i gang.
              </p>

              <div className="space-y-3 pt-2">
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 shrink-0 mt-0.5">
                    <Check className="h-3 w-3 stroke-[3]" />
                  </div>
                  <span className="text-sm text-stone-700"><strong>100% uforpligtende:</strong> Det er helt gratis at få lavet en visualisering.</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 shrink-0 mt-0.5">
                    <Check className="h-3 w-3 stroke-[3]" />
                  </div>
                  <span className="text-sm text-stone-700"><strong>Flere farveforslag:</strong> Vi viser dig typisk 2-3 forskellige stenblandinger.</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 shrink-0 mt-0.5">
                    <Check className="h-3 w-3 stroke-[3]" />
                  </div>
                  <span className="text-sm text-stone-700"><strong>Hurtigt svar:</strong> Du modtager dit visualiserede billede direkte i din indbakke.</span>
                </div>
              </div>
            </div>

            {/* Vusion Formular */}
            <div className="bg-white p-6 sm:p-8 rounded-2xl border border-stone-200 shadow-md">
              <h3 className="text-xl font-bold text-stone-900 mb-4 flex items-center gap-2">
                <ImageIcon className="h-5 w-5 text-emerald-600" />
                Få en gratis visualisering
              </h3>

              {status === 'success' ? (
                <div className="bg-emerald-50 border border-emerald-200 p-6 rounded-xl text-center space-y-3">
                  <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" strokeWidth={1.5} />
                  <h4 className="font-bold text-emerald-900 text-lg">Tak for din forespørgsel!</h4>
                  <p className="text-sm text-emerald-700">
                    Vi har modtaget dit billede og dine oplysninger. Vores designere går i gang med din digitale visualisering, og du vil høre fra os hurtigst muligt.
                  </p>
                  <button
                    onClick={() => {
                      setStatus('idle');
                      setMessageText('');
                    }}
                    className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-emerald-700 transition-colors"
                  >
                    Send endnu et billede
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Honeypot field for anti-spam */}
                  <input
                    type="text"
                    name="website_url"
                    className="sr-only absolute"
                    tabIndex={-1}
                    autoComplete="off"
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-1">Navn *</label>
                      <input
                        type="text"
                        name="navn"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                        placeholder="Dit fulde navn"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-1">Telefon *</label>
                      <input
                        type="tel"
                        name="telefon"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                        placeholder="Dit telefonnummer"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-1">E-mail *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      placeholder="din@email.dk"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-1">Besked / Ønsker til farve</label>
                    <textarea
                      name="besked"
                      rows="3"
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      placeholder="Fortæl os lidt om dit projekt og hvilke farver du overvejer (f.eks. lyse, grå eller varme sandfarver)..."
                    ></textarea>
                  </div>

                  {/* File Upload Area */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-1">Upload billede af dit område *</label>
                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-stone-200 border-dashed rounded-xl hover:border-emerald-500 transition-colors cursor-pointer relative">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        multiple
                        accept=".png,.jpg,.jpeg,.webp"
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <div className="space-y-1 text-center">
                        <Upload className="mx-auto h-10 w-10 text-stone-400" />
                        <div className="flex text-sm text-stone-600 justify-center">
                          <span className="relative font-semibold text-emerald-600 hover:text-emerald-700">Klik for at uploade</span>
                          <p className="pl-1">eller træk og slip</p>
                        </div>
                        <p className="text-xs text-stone-400">PNG, JPG, WEBP op til 10 MB pr. billede (maks 5 billeder)</p>
                      </div>
                    </div>

                    {/* Selected Files Preview */}
                    {selectedFiles.length > 0 && (
                      <div className="mt-3 space-y-2">
                        <p className="text-xs font-bold uppercase tracking-wider text-stone-700">Valgte billeder ({selectedFiles.length}):</p>
                        <div className="grid grid-cols-1 gap-2">
                          {selectedFiles.map((file, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-stone-50 rounded-lg border border-stone-200 text-xs text-stone-700">
                              <span className="truncate max-w-[200px] font-medium">{file.name}</span>
                              <div className="flex items-center gap-2">
                                <span>{(file.size / (1024 * 1024)).toFixed(2)} MB</span>
                                <button
                                  type="button"
                                  onClick={() => removeFile(idx)}
                                  className="p-1 text-stone-400 hover:text-red-500 rounded-full hover:bg-stone-100"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {status === 'error' && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2.5 text-xs text-red-700">
                      <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                      <span>{errorMessage}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting' || selectedFiles.length === 0}
                    className="w-full flex items-center justify-center px-4 py-3 rounded-xl text-sm font-bold uppercase tracking-wider text-white bg-stone-900 hover:bg-stone-800 disabled:bg-stone-300 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    {status === 'submitting' ? (
                      <>
                        <Loader2 className="animate-spin h-4 w-4 mr-2" />
                        Sender billeder...
                      </>
                    ) : (
                      'Send billeder og få visualisering'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-stone-900 text-white py-16 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold">Klar til at tage næste skridt?</h2>
          <p className="text-stone-400 mt-4 max-w-2xl mx-auto">
            Kontakt os i dag for at få tilsendt fysiske stenprøver, eller få et uforpligtende tilbud på din nye, eksklusive belægning.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center px-6 py-3 rounded-xl font-bold bg-emerald-600 text-white hover:bg-emerald-700 hover:-translate-y-1 transition-all duration-300"
            >
              Få et tilbud & stenprøver
            </Link>
            <Link
              to="/referencer"
              className="inline-flex items-center justify-center px-6 py-3 rounded-xl font-bold bg-stone-800 hover:bg-stone-700 border border-stone-700 hover:-translate-y-1 transition-all duration-300"
            >
              Se referencer
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
